import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, ArrowLeft, LayoutGrid } from "lucide-react";
import { adminAPI } from "../../services/api/admin";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import MetricForm from "../../components/metrics/MetricForm";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";

const resultBadge = (rt) =>
  ({ boolean: "secondary", rating: "info", enum: "warning", numeric: "muted" }[rt] ||
  "muted");

const AdminMetricsGallery = () => {
  const { currentWorkspace } = useAuth();
  const { showToast } = useToast();

  const [mode, setMode] = useState("list"); // list | create | edit
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getGalleryMetrics();
      setMetrics(res.data || []);
    } catch (e) {
      showToast("Failed to load gallery metrics", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (mode === "list") fetchMetrics();
  }, [mode, fetchMetrics]);

  const handleSubmit = async (payload) => {
    try {
      if (mode === "edit" && editing) {
        await adminAPI.updateGalleryMetric(editing.id, payload);
        showToast("Gallery metric updated", "success");
      } else {
        await adminAPI.createGalleryMetric(payload);
        showToast("Gallery metric created", "success");
      }
      setEditing(null);
      setMode("list");
    } catch (e) {
      showToast(
        e.response?.data?.error?.[0] ||
          e.response?.data?.error ||
          "Failed to save gallery metric",
        "error",
      );
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete gallery metric “${m.name}”? This won't affect copies already added to workspaces.`))
      return;
    try {
      await adminAPI.deleteGalleryMetric(m.id);
      showToast("Deleted", "success");
      fetchMetrics();
    } catch (e) {
      showToast("Failed to delete", "error");
    }
  };

  if (mode === "create" || mode === "edit") {
    return (
      <div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setMode("list");
          }}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to gallery
        </button>
        <h1 className="text-base font-semibold text-foreground mb-1">
          {mode === "edit" ? "Edit gallery metric" : "New gallery metric"}
        </h1>
        <p className="ui-subtitle mb-4">
          This template appears in every workspace's Metrics Gallery for users to
          add.
        </p>
        <MetricForm
          mode={mode}
          initialMetric={editing}
          showAssignment={false}
          canWrite
          clientId={currentWorkspace?.id}
          submitLabel={mode === "edit" ? "Save template" : "Create template"}
          onSubmit={handleSubmit}
          onCancel={() => {
            setEditing(null);
            setMode("list");
          }}
          notify={showToast}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-foreground">
            Metrics Gallery
          </h1>
          <p className="ui-subtitle">
            Curated metric templates available to all workspaces.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setMode("create")}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="size-4" /> New gallery metric
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-foreground" />
        </div>
      ) : metrics.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-border py-20 text-center">
          <div className="mb-3 rounded-full bg-muted p-4">
            <LayoutGrid className="h-8 w-8 text-muted-foreground" />
          </div>
          <h2 className="text-sm font-semibold text-foreground">
            No gallery metrics yet
          </h2>
          <p className="ui-subtitle mt-1">
            Create a template to make it available across all workspaces.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Result</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {metrics.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="font-medium text-foreground">
                    {m.name}
                  </TableCell>
                  <TableCell className="max-w-md whitespace-normal text-muted-foreground">
                    {m.description}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {m.metrics_type}
                  </TableCell>
                  <TableCell>
                    <Badge variant={resultBadge(m.result_type)}>
                      {m.result_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        title="Edit"
                        onClick={() => {
                          setEditing(m);
                          setMode("edit");
                        }}
                        className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        onClick={() => handleDelete(m)}
                        className="rounded p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default AdminMetricsGallery;
